
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PoundSterling, TrendingUp, Calculator, PieChart, Download, Plus } from "lucide-react";
import NavBar from "@/components/NavBar";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

export default function FinancialAssessment() {
  const [financials, setFinancials] = useState({
    revenue: "",
    costs: "",
    initialInvestment: "",
    workingCapital: "",
    taxRate: "19",
    wacc: "8"
  });

  const calculateMetrics = () => {
    const rev = parseFloat(financials.revenue) || 0;
    const costs = parseFloat(financials.costs) || 0;
    const investment = parseFloat(financials.initialInvestment) || 0;
    const tax = parseFloat(financials.taxRate) / 100;
    
    const ebit = rev - costs;
    const ebitda = ebit; // Simplified
    const netIncome = ebit * (1 - tax);
    const roe = investment > 0 ? (netIncome / investment) * 100 : 0;
    
    return { ebit, ebitda, netIncome, roe };
  };

  const metrics = calculateMetrics();

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <PoundSterling className="h-8 w-8 text-primary" />
            Financial Assessment
          </h1>
          <p className="text-muted-foreground">
            Comprehensive financial modeling and analysis tools for project evaluation
          </p>
        </div>

        <Tabs defaultValue="modeling" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="modeling">Financial Modeling</TabsTrigger>
            <TabsTrigger value="ratios">Financial Ratios</TabsTrigger>
            <TabsTrigger value="forecasting">Cash Flow Forecasting</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="modeling" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5" />
                    Financial Inputs
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="revenue">Annual Revenue (£)</Label>
                    <Input
                      id="revenue"
                      type="number"
                      placeholder="2000000"
                      value={financials.revenue}
                      onChange={(e) => setFinancials({...financials, revenue: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="costs">Annual Operating Costs (£)</Label>
                    <Input
                      id="costs"
                      type="number"
                      placeholder="1500000"
                      value={financials.costs}
                      onChange={(e) => setFinancials({...financials, costs: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="initialInvestment">Initial Investment (£)</Label>
                    <Input
                      id="initialInvestment"
                      type="number"
                      placeholder="5000000"
                      value={financials.initialInvestment}
                      onChange={(e) => setFinancials({...financials, initialInvestment: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="workingCapital">Working Capital (£)</Label>
                    <Input
                      id="workingCapital"
                      type="number"
                      placeholder="300000"
                      value={financials.workingCapital}
                      onChange={(e) => setFinancials({...financials, workingCapital: e.target.value})}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Financial Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tax Rate (%)</Label>
                      <Input
                        type="number"
                        value={financials.taxRate}
                        onChange={(e) => setFinancials({...financials, taxRate: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>WACC (%)</Label>
                      <Input
                        type="number" 
                        step="0.1"
                        value={financials.wacc}
                        onChange={(e) => setFinancials({...financials, wacc: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="pt-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">EBIT:</span>
                      <Badge variant="secondary">£{metrics.ebit.toLocaleString()}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Net Income:</span>
                      <Badge variant="default">£{metrics.netIncome.toLocaleString()}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">ROE:</span>
                      <Badge variant="outline">{metrics.roe.toFixed(2)}%</Badge>
                    </div>
                    <Progress value={Math.min(Math.max(metrics.roe, 0), 100)} className="w-full" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="ratios" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Profitability Ratios</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span>Gross Margin:</span>
                    <span className="font-semibold">
                      {financials.revenue ? (((parseFloat(financials.revenue) - parseFloat(financials.costs || "0")) / parseFloat(financials.revenue)) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Operating Margin:</span>
                    <span className="font-semibold">
                      {financials.revenue ? ((metrics.ebit / parseFloat(financials.revenue)) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Net Margin:</span>
                    <span className="font-semibold">
                      {financials.revenue ? ((metrics.netIncome / parseFloat(financials.revenue)) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Return Ratios</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span>ROE:</span>
                    <span className="font-semibold">{metrics.roe.toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>ROI:</span>
                    <span className="font-semibold">
                      {financials.initialInvestment ? ((metrics.netIncome / parseFloat(financials.initialInvestment)) * 100).toFixed(2) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>ROIC:</span>
                    <span className="font-semibold">
                      {financials.initialInvestment ? ((metrics.ebit / parseFloat(financials.initialInvestment)) * 100).toFixed(2) : 0}%
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Efficiency Ratios</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span>Asset Turnover:</span>
                    <span className="font-semibold">
                      {financials.initialInvestment ? (parseFloat(financials.revenue || "0") / parseFloat(financials.initialInvestment)).toFixed(2) : 0}x
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Working Capital Ratio:</span>
                    <span className="font-semibold">
                      {financials.workingCapital ? (parseFloat(financials.revenue || "0") / parseFloat(financials.workingCapital)).toFixed(2) : 0}x
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cost Efficiency:</span>
                    <span className="font-semibold">
                      {financials.revenue ? ((parseFloat(financials.costs || "0") / parseFloat(financials.revenue)) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="forecasting" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>5-Year Cash Flow Forecast</span>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Scenario
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Year</th>
                        <th className="text-right p-2">Revenue</th>
                        <th className="text-right p-2">Operating Costs</th>
                        <th className="text-right p-2">EBITDA</th>
                        <th className="text-right p-2">Cash Flow</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3, 4, 5].map((year) => {
                        const growthRate = 0.05; // 5% growth
                        const yearRevenue = parseFloat(financials.revenue || "0") * Math.pow(1 + growthRate, year - 1);
                        const yearCosts = parseFloat(financials.costs || "0") * Math.pow(1 + growthRate, year - 1);
                        const yearEbitda = yearRevenue - yearCosts;
                        const yearCashFlow = yearEbitda * 0.8; // Simplified
                        
                        return (
                          <tr key={year} className="border-b">
                            <td className="p-2">{year}</td>
                            <td className="text-right p-2">£{yearRevenue.toLocaleString()}</td>
                            <td className="text-right p-2">£{yearCosts.toLocaleString()}</td>
                            <td className="text-right p-2">£{yearEbitda.toLocaleString()}</td>
                            <td className="text-right p-2 font-semibold">£{yearCashFlow.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Financial Assessment Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Financial Model</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Ratio Analysis</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Cash Flow Forecast</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Executive Summary</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
